import * as React from "react";
import { t } from "../i18n.js";
import { McTally, LocaleSwitcher, LiveRegion, JoinCard, MeanNote, OmittedNote, OpenEntryText, WithheldNote } from "../components.js";
import { describeAggregate, describeLeaderboard } from "../announce.js";
import { revealRows, visibleRows, type QuestionAggregate } from "../api.js";
import { useRoom, advertisableCode } from "../useRoom.js";

/** How fast names flick past during a draw. Fast enough to read as motion, slow enough to read. */
const SPIN_TICK_MS = 120;

/**
 * Does this viewer want no motion?
 *
 * Read once per mount rather than subscribed to: a projector's OS setting does not change during a
 * round, and a `matchMedia` listener here would buy a re-render for a case that never happens.
 * Guarded for `matchMedia` being absent, because this module is also imported under jsdom and in
 * the prerender pass.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * The spin (roadmap W5, AC5). The names cycle in big type while the server holds the real answer —
 * the cycling is decoration over a decision already made, which is exactly why it can be dropped.
 *
 * With reduced motion on, there is no interval at all: a static label, not a slower flicker.
 * ~35% of adults over 40 report motion sensitivity, and a name strobing at 8Hz on a wall-sized
 * screen is the worst version of it in the whole product.
 */
export function Spinner({ candidates }: { candidates: string[] }): React.ReactElement {
  const still = prefersReducedMotion();
  const [i, setI] = React.useState(0);
  React.useEffect(() => {
    if (still || candidates.length === 0) return;
    const id = setInterval(() => setI((n) => n + 1), SPIN_TICK_MS);
    return () => clearInterval(id);
  }, [still, candidates.length]);

  if (still || candidates.length === 0) {
    return <p className="font-display text-display-xl text-accent">{t("picker.spinning")}</p>;
  }
  return (
    <div className="flex flex-col gap-4">
      {/* aria-hidden: the cycling name is meaningless to a screen reader and would fight the live
          region, which already says how many are in the draw. */}
      <p className="font-display text-display-xl" aria-hidden="true">
        {candidates[i % candidates.length]}
      </p>
      <p className="font-display text-display-sm text-accent">{t("picker.spinning")}</p>
    </div>
  );
}

/** The drawn result, big. One name, teams as columns, or a numbered order. */
export function DrawnResult({ agg }: { agg: Extract<QuestionAggregate, { type: "picker"; phase: "drawn" }> }): React.ReactElement {
  if (agg.pick === "teams") {
    return (
      <div className="flex flex-wrap gap-4">
        {agg.teams.map((team, i) => (
          <div key={i} className="min-w-40 flex-1 rounded-md bg-surface p-4">
            <h2 className="font-display text-display-sm text-accent">
              {t("picker.team")} {i + 1}
            </h2>
            {team.map((name, j) => (
              // Index keys on purpose: two people can share a nickname, and a repeated key drops a row.
              <p key={j} className="text-display-sm">
                {name}
              </p>
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (agg.pick === "rotation") {
    return (
      <div className="flex flex-col gap-3 text-display-sm">
        {agg.order.map((name, i) => (
          <div key={i} className="flex gap-4 rounded-md bg-surface p-4">
            <span className="font-bold text-accent">{i + 1}.</span>
            <span>{name}</span>
          </div>
        ))}
      </div>
    );
  }
  return (
    <p className="font-display text-display-xl">
      {agg.drawn ?? t("picker.nobody")}
    </p>
  );
}

/**
 * A list on the wall (W10a, US-LV-17 AC4).
 *
 * Two columns past ten rows and capped at twenty, because the failure this fixes is measured, not
 * theoretical: a forty-writer room projects forty rows of up to 120 characters and the back row
 * reads none of them. The cut is ANNOUNCED — a silently shortened list on the projector is the same
 * broken promise as a silently shortened ballot on the server.
 *
 * Rows come in already ordered (votes/upvotes first); this only cuts, never re-sorts. The phone
 * does not use it — a phone scrolls.
 */
export function ProjectionList<T>({
  rows,
  render,
  pin,
  omitted,
}: {
  rows: T[];
  render: (row: T, i: number) => React.ReactNode;
  /** Rows that must survive the cap — see `visibleRows`. Q&A highlights, and nothing else so far. */
  pin?: (row: T) => boolean;
  /**
   * Votable entries the ROUND dropped (the promptvote `omitted`) — a different fact from the rows
   * this SCREEN dropped, and NOT the same number: a host hiding three ballot entries after the
   * ballot froze leaves 22 rows at reveal (two cut by the cap) against five never balloted. Both
   * are said, in one line, each labelled.
   */
  omitted?: number | undefined;
}): React.ReactElement {
  const { shown, more } = visibleRows(rows, 20, pin);
  const notice = [
    more > 0 ? t(more === 1 ? "projection.moreOne" : "projection.more", { n: String(more) }) : null,
    omitted ? t(omitted === 1 ? "pv.omittedOne" : "pv.omitted", { n: String(omitted) }) : null,
  ].filter((part): part is string => part !== null);
  return (
    <div className="flex flex-col gap-3">
      {/*
        CSS multicol, not a grid: a grid is row-major, so a ranked list reads 1,2 / 3,4 across the
        wall instead of down one column and then the other. `columns-2` fills the first column
        first, which is the order the room is being asked to read. `break-inside-avoid` on the rows
        keeps an entry from being sliced in half at the column boundary.
      */}
      <div
        className={`text-display-sm [&>*]:mb-3 [&>*]:break-inside-avoid ${
          shown.length > 10 ? "md:columns-2 md:gap-6" : ""
        }`}
      >
        {shown.map((row, i) => render(row, i))}
      </div>
      {/* ONE line, the parts that apply, in display type: what the SCREEN cut, then what the ROUND
          cut. Neither suppresses the other — suppressing the generic line whenever `omitted` was
          set meant a moderated round said nothing about the rows it had actually dropped. */}
      {notice.length > 0 && (
        <p className="font-display text-display-sm text-accent">{notice.join(" · ")}</p>
      )}
    </div>
  );
}

/**
 * The projection's top bar.
 *
 * Exported for the same reason `ProjectionList` is: the compact join card is an ACCEPTANCE
 * criterion — a latecomer must be able to join while a round is running, without the host stopping
 * to read the code out — and `Projection` itself cannot be rendered in a test, because mounting it
 * opens a WebSocket.
 */
export function ProjectionHeader({ joinCode, roundOpen }: { joinCode: string; roundOpen: boolean }): React.ReactElement {
  return (
    <header className="flex flex-wrap items-center justify-between gap-4">
      <span className="font-display text-display-md">Klapzo</span>
      {/* Compact while a round runs. Large below when there is nothing on screen to compete with it.
          `joinCode` is already `advertisableCode`'d by the caller, so an over session passes "" and
          `JoinCard` draws nothing — the gate is one rule in one place, not a prop per placement. */}
      {roundOpen && <JoinCard code={joinCode} compact />}
      <LocaleSwitcher />
    </header>
  );
}

/** Big-screen projection (WCAG AA: high contrast, black on white + brand fills). */
export function Projection({ sessionId }: { sessionId: string }): React.ReactElement {
  // `observer`: a wall never answers, and the queue's key is the path — which a phone in the same
  // browser shares byte for byte.
  const { state } = useRoom(`/sessions/${sessionId}/connect`, { observer: true });
  const agg = state.aggregate;
  // Once, for both placements: a code the room can no longer be joined with is not a code.
  const code = advertisableCode(state.status, state.joinCode, "wall");
  const showLeaderboard = (state.leaderboard.length > 0 || state.teamBoard.length > 0) && (state.closed || !state.question);
  // One spoken sentence for the whole view. The leaderboard wins when it is the thing on screen.
  const spoken = showLeaderboard
    ? describeLeaderboard(state.leaderboard, state.teamBoard)
    : describeAggregate(agg, { options: state.question?.options ?? [], question: state.question, closed: state.closed });

  return (
    <div className="mx-auto flex min-h-full max-w-5xl flex-col gap-8 p-10">
      <ProjectionHeader joinCode={code} roundOpen={state.question !== null} />
      <LiveRegion text={spoken} />

      {!state.question && (
        <div className="mt-10 flex flex-col items-center gap-8">
          {!showLeaderboard && (
            <p className="text-center font-display text-display-lg">{t("projection.waiting")}</p>
          )}
          <JoinCard code={code} />
        </div>
      )}

      {state.question && (
        <>
          {state.race && !state.race.ended && (
            <p className="font-display text-display-sm text-accent">
              {t("presenter.raceRound")} {state.race.round}/{state.race.total}
            </p>
          )}
          <h1 className="font-display text-display-xl">{state.question.prompt}</h1>
          {state.closed && <p className="font-display text-display-sm text-accent">{t("common.closed")}</p>}
          {/* A single-screen round (US-LV-22): nobody answers from a phone, so say what is happening. */}
          {state.question.hosted === true && (
            <p className="font-display text-display-sm text-accent">
              {t(state.closed ? "hosted.recorded" : "hosted.caption")}
            </p>
          )}

          {agg?.type === "mc" && agg.tally === "open" && (
            <div className="flex flex-col gap-4 text-display-sm">
              <McTally options={state.question.options ?? []} agg={agg} />
              {agg.payload !== undefined && (
                <p className="text-accent">
                  {t("prio.unit")} · {agg.total} {t(agg.total === 1 ? "mc.answeredOne" : "mc.answered")}
                </p>
              )}
              {/* The headline of a scale round, beside the bars it was computed from (W9b). The
                  scale points come off the `question` frame: a LIVE aggregate carries none. */}
              <MeanNote agg={agg} options={state.question.options} />
            </div>
          )}

          {/* Tally withheld: show the options and how many have answered, never the split. */}
          {agg?.type === "mc" && agg.tally === "hidden" && (
            <div className="flex flex-col gap-4 text-display-sm">
              {state.question.options?.map((o) => (
                <div key={o.id} className="rounded-md bg-surface p-4">{o.label}</div>
              ))}
              <p className="text-accent">
                {agg.total} {t(agg.total === 1 ? "mc.answeredOne" : "mc.answered")} · {t("mc.tallyHidden")}
              </p>
            </div>
          )}

          {/* A withheld cloud/wall is EMPTY on the wire, so the wall says why (US-LV-20 AC5) —
              an empty projection is otherwise read as "nobody answered". */}
          <WithheldNote
            question={state.question}
            closed={state.closed}
            aggregate={agg}
            className="font-display text-display-sm text-accent"
          />
          {agg?.type === "wordcloud" && (
            <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
              {agg.words.map((w) => (
                <span key={w.word} className="font-display" style={{ fontSize: `${1.5 + Math.min(w.count, 10) * 0.5}rem` }}>
                  {w.word}
                </span>
              ))}
            </div>
          )}

          {/* Prompt-vote, phase by phase. During `write` the wire carries only a count — there is no
              text field to render even by mistake, which is the point of the phase-split type. */}
          {agg?.type === "promptvote" && agg.phase === "write" && (
            <p className="font-display text-display-lg">
              {agg.submissions} {t(agg.submissions === 1 ? "pv.submissionOne" : "pv.submissions")}
            </p>
          )}
          {agg?.type === "promptvote" && agg.phase === "vote" && (
            <>
              <p className="font-display text-display-sm text-accent">
                {t("pv.vote")} · {agg.votesCast} {t("pv.votesCast")}
              </p>
              {/* The vote list IS the ballot, so `more` (rows this screen cut) and `omitted` (entries
                  the ROUND cut) are different facts here and both are worth saying. */}
              <ProjectionList
                rows={agg.ballot}
                render={(entry) => (
                  <div key={entry.id} className="rounded-md bg-surface p-4">
                    {entry.text}
                  </div>
                )}
              />
              <OmittedNote n={agg.omitted} className="font-display text-display-sm text-accent" />
            </>
          )}
          {agg?.type === "promptvote" && agg.phase === "reveal" && (
            <ProjectionList
              rows={revealRows(agg)}
              // At reveal every votable entry is ranked, so the tail the cap cuts is exactly the
              // entries the ballot omitted: ONE sentence, and the honest one.
              omitted={agg.omitted}
              render={(r, i) => (
                <div
                  key={r.id}
                  className={`flex justify-between rounded-md p-4 ${i === 0 ? "bg-primary text-primary-foreground" : "bg-surface"}`}
                >
                  <span>
                    {i === 0 && <span aria-hidden="true">{"\u2605 "}</span>}
                    {r.text}
                    {r.author !== null && <> — <b>{r.author}</b></>}
                    {i === 0 && <span className="sr-only"> ({t("a11y.winner")})</span>}
                  </span>
                  <span className="font-bold">
                    {r.votes} {t(r.votes === 1 ? "pv.voteOne" : "pv.votes")}
                  </span>
                </div>
              )}
            />
          )}

          {agg?.type === "picker" && agg.phase === "spinning" && <Spinner candidates={agg.candidates} />}
          {agg?.type === "picker" && agg.phase === "drawn" && <DrawnResult agg={agg} />}

          {agg?.type === "open" && (
            <ProjectionList
              rows={agg.entries}
              // A highlight exists to put an entry ON THE WALL. Q&A is ordered by upvotes alone, so
              // "let's take this one" on a question nobody upvoted sorts last and the cap ate it.
              pin={(e) => e.highlighted}
              render={(e) => (
                <div key={e.id} className={`rounded-md p-4 ${e.highlighted ? "bg-primary text-primary-foreground" : "bg-surface"}`}>
                  {e.highlighted && <span aria-hidden="true">{"\u2605 "}</span>}
                  ▲ {e.upvotes} — <OpenEntryText entry={e} />
                  {e.highlighted && <span className="sr-only"> ({t("a11y.highlighted")})</span>}
                </div>
              )}
            />
          )}
        </>
      )}

      {showLeaderboard && (
        // Beside, not below: the whole finding behind W7 is that the micro board next to the macro one
        // beats the macro one alone at every skill level. Stacked on a projector it is a footnote.
        // `flex-wrap` is what keeps that true on a 4:3 room projector — the columns stack rather than
        // squeezing a ten-row board into a third of the width.
        <div className="flex flex-wrap gap-8">
          <div className="flex min-w-72 flex-1 flex-col gap-3">
            <h2 className="font-display text-display-lg">{t("common.leaderboard")}</h2>
            {state.leaderboard.slice(0, 10).map((e, i) => (
              <div key={i} className="flex justify-between rounded-md bg-surface p-4 text-display-sm">
                <span>{e.rank}. {e.nickname}</span>
                <span className="font-bold">{e.score}</span>
              </div>
            ))}
          </div>
          {state.teamBoard.length > 0 && (
            <div className="flex min-w-72 flex-1 flex-col gap-3">
              <h2 className="font-display text-display-lg">{t("teams.standings")}</h2>
              {state.teamBoard.map((row) => (
                <div key={row.id} className="flex justify-between rounded-md bg-surface p-4 text-display-sm">
                  <span>
                    {row.rank}. {row.name}
                    {/* Size, never members: this screen is the one the whole room is looking at. */}
                    <span className="sr-only">
                      {" "}
                      ({t(row.size === 1 ? "teams.sizeOne" : "teams.size", { n: String(row.size) })})
                    </span>
                  </span>
                  <span className="font-bold">{row.score}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
